<template>
  <!-- Table Container Card -->
  <div>
    <b-card
      no-body
    >
      <gig-orders-list
        v-if="userRole === 'admin'"
        :order-table-columns="orderTableColumns"
        :suggested-columns="suggestedColumns"
        @fetch-orders="fetchOrders"
        @accept-suggestion="createFromSuggestion"
      />
    </b-card>

    <b-card
      no-body
    >
      <gig-list-add-new
        :edit-gig="editedGig"
        @search-location="onSearchLocation"
        @create-gig="onGigCreateEdit"
        @modal-open="onModalOpen"
        @modal-close="onModalClose"
        @fetch-user-upcoming-shifts="fetchUserShifts"
      />

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <b-button
              v-b-modal.create-gig-modal
              variant="primary"
            >
              {{ $t('gig_page.btn_add_gig') }}
            </b-button>
          </b-col>

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <v-select
                v-model="statusFilter"
                :dir="$store.state.hwUiLayout.isRTL ? 'rtl' : 'ltr'"
                :options="statusOptions"
                style="text-transform: capitalize"
                class="invoice-filter-select"
                :placeholder="$t('gig_page.select_status')"
                :clearable="false"
              >
                <template #selected-option="{ label }">
                  <span class="text-truncate overflow-hidden">
                    {{ label }}
                  </span>
                </template>
              </v-select>
            </div>
          </b-col>
        </b-row>

      </div>

      <b-table
        ref="refInvoiceListTable"
        :items="fetchGigs"
        responsive
        :fields="tableColumns"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        :empty-text="$t('no_matching_records')"
        :sort-desc.sync="isSortDirDesc"
        class="position-relative"
      >

        <template #head(invoiceStatus)>
          <feather-icon
            icon="TrendingUpIcon"
            class="mx-auto"
          />
        </template>

        <!-- Column: created -->
        <template #cell(created)="data">
          <span
            class="font-weight-bold"
          >
            {{ data.value.toDate().toLocaleDateString('fi') }}
          </span>
        </template>

        <!-- Column: title -->
        <template #cell(title)="data">
          <b-link
            v-b-modal.create-gig-modal
            class="font-weight-bold"
            @click="onClick(data.item)"
          >
            {{ data.value }}
          </b-link>
        </template>

        <!-- Column: Start date -->
        <template #cell(startDate)="data">
          <span
            class="font-weight-bold"
          >
            {{ data.value.toDate().toLocaleDateString('fi') }}
          </span>
        </template>
        <!-- Column: Exp date -->
        <template #cell(expDate)="data">
          <span
            class="font-weight-bold"
          >
            {{ data.value.toDate().toLocaleDateString('fi') }}
          </span>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="">
          <b-badge
            v-if="!isLoading"
            pill
            :variant="statusVariant"
            :style="`text-transform: uppercase; margin-block: 10px; ${statusFilter.value === 'expired' && 'color: #4B4B4B !important'}`"
          >
            {{ statusLabel }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="{ item }">

          <div class="text-nowrap">
            <!-- Dropdown -->
            <b-dropdown
              variant="link"
              toggle-class="p-0"
              no-caret
              :right="$store.state.hwUiLayout.isRTL"
              @show="checkCanDelete(item.id)"
            >

              <template #button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="align-middle text-body"
                />
              </template>
              <b-dropdown-item
                v-b-modal.create-gig-modal
                @click="onClick(item)"
              >
                <feather-icon icon="EditIcon" />
                <span class="align-middle ml-50">{{ $t('edit') }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-b-modal.modal-danger
                @click="onDeleteGig(item.id)"
              >
                <feather-icon icon="TrashIcon" />
                <span class="align-middle ml-50">{{ $t('delete') }}</span>
              </b-dropdown-item>
            </b-dropdown>
            <b-button
              v-if="userRole === 'admin' && item && item.viewDetail"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              v-b-tooltip.hover
              variant="link-secondary"
              :title="`${item.viewDetail.count || 0} ${$t('gig_page.views')}`"
              class="p-0 ml-50"
              @click="showUserViews(item, () => $bvModal.show('user-views-modal'))"
            >
              <b-badge
                pill 
                :variant="item.viewDetail.count > 0 ? 'light-info' : 'light-secondary'"
                class="mr-50"
              >
                {{ item.viewDetail.count }}
                <feather-icon
                  icon="EyeIcon"
                  size="16"
                />
              </b-badge>
            </b-button>
          </div>
        </template>

      </b-table>
    </b-card>
    <b-modal
      id="modal-danger"
      ok-only
      :ok-variant="canDelete ? 'danger' : ''"
      :ok-title="canDelete ? $t('delete') : $t('close')"
      :modal-class="canDelete ? 'modal-danger' : 'modal-warning'"
      centered
      :title="$t('gig_page.title_delete_gig_shifts')"
      @ok="canDelete ? confirmDelete(processer) : null"
    >
      <b-card-text>
        {{ canDelete ? $t('gig_page.content_delete_gig_shifts') : $t('gig_page.content_cannot_delete_shift') }}
      </b-card-text>
    </b-modal>

    <user-views
      :users.sync="userViews"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BButton, BTable, BLink,
  BBadge, BDropdown, BDropdownItem, VBModal,
  BCardText, BModal, VBTooltip,
} from 'bootstrap-vue'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@hw-ui/utils/filter'
import vSelect from 'vue-select'
import Ripple from 'vue-ripple-directive'
import useGigList from './useGigList'
import GigOrdersList from './GigOrdersList.vue'
import GigListAddNew from './GigListAddNew.vue'
import UserViews from './UserViews.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BCardText,
    GigListAddNew,
    UserViews,
    GigOrdersList, // TODO currently unused 
    BModal,
    
    vSelect,
  },
  directives: {
    'b-modal': VBModal,
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      editedGig: null,
      selectedShifts: null,
      userRole: null,
    }
  },
  computed: {
    statusVariant() {
      const statusColor = {
        draft: 'light-secondary',
        open: 'light-success',
        suggestion: 'light-warning',
        expired: 'light',
      }
      return statusColor[this.statusFilter.value]
    },
    statusLabel() {
      if (this.statusFilter.value === 'open') {
        return this.$t('gig_page.published')
      }
      return this.$t(`gig_page.${this.statusFilter.value}`)
    },
    appConfig() {
      return this.$store.state.app.appConfig || {}
    },
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser')
      .then(res => {
        const user = JSON.parse(JSON.stringify(res))
        this.processer = {
          firstName: user.firstName,
          lastName: user.lastName,
          role: user.role,
        }
        this.userRole = user.role
      })
  },
  methods: {
    onLoadMore() {
      this.currentPage += 1
      this.loadMore = true
    },
    
  },
  setup() {

    function onClick(gig) {
      this.editedGig = gig
    }

    function onModalClose() {
      this.editedGig = null
    }


    const {
      fetchGigs,
      tableColumns,
      perPage,
      currentPage,
      totalInvoices,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInvoiceListTable,
      loadMore,
      canLoadMore,

      statusFilter,
      statusOptions,

      refetchData,

      resolveInvoiceStatusVariantAndIcon,
      resolveClientAvatarVariant,
      onModalOpen,
      onGigCreateEdit,
      onSearchLocation,
      onDeleteGig,
      confirmDelete,
      canDelete,
      checkCanDelete,
      createFromSuggestion,
      orderTableColumns,
      suggestedColumns,
      fetchOrders,
      createDraftOrder,
      getSuggestions,
      
      changePage,
      isLoading,
      userViews,
      showUserViews,
      fetchUserShifts,
    } = useGigList()

    return {
      fetchGigs,
      tableColumns,
      perPage,
      currentPage,
      totalInvoices,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refInvoiceListTable,
      loadMore,
      canLoadMore,
      createFromSuggestion,
      statusFilter,

      refetchData,

      statusOptions,

      avatarText,
      resolveInvoiceStatusVariantAndIcon,
      resolveClientAvatarVariant,
      onModalOpen,
      onModalClose,
      onGigCreateEdit,
      onSearchLocation,
      onDeleteGig,
      confirmDelete,
      onClick,
      canDelete,
      checkCanDelete,

      orderTableColumns,
      suggestedColumns,
      fetchOrders,
      createDraftOrder,
      getSuggestions,

      changePage,
      isLoading,
      userViews,
      showUserViews,
      fetchUserShifts,
    }
  },
}
</script>

<style lang="scss">
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
.capitalize {
  text-transform: capitalize
}
</style>

<style lang="scss">
@import '@hw-ui/scss/vue/libs/vue-select.scss';
</style>
